import { motion } from 'framer-motion';
import {
    ArrowDownUp,
    Calendar,
    CalendarDays,
    ChevronRight,
    FileSearch,
    Filter,
    IndianRupee,
    Link2,
    LoaderCircle,
    ReceiptText,
    RefreshCcw,
    Search,
    SlidersHorizontal,
    Tag,
    X,
} from 'lucide-react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DateRangeSelector from '../../Components/UI/DateRangeSelector';
import { BASE_URL_API } from '../../References/URLs';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import AuthContext from '../../store/auth-context';
import styles from './ExpensesHistory.module.css';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const SORT_BY_OPTIONS = [
    { value: 'expenseDate', label: 'Bill Date' },
    { value: 'totalExpense', label: 'Total Amount' },
    { value: 'createdOn', label: 'Created On' },
    { value: 'balanceDue', label: 'Balance Due' },
];

const SORT_ORDER_OPTIONS = [
    { value: 'DESC', label: 'Newest / Highest First' },
    { value: 'ASC', label: 'Oldest / Lowest First' },
];

const PAYMENT_STATUS_OPTIONS = [
    { value: '', label: 'All Payment Status' },
    { value: 'unpaid', label: 'Unpaid' },
    { value: 'partial', label: 'Partial' },
    { value: 'paid', label: 'Paid' },
];

const EXPENSE_TYPE_OPTIONS = [
    { value: '', label: 'All Expense Types' },
    { value: 'opex', label: 'OpEx' },
    { value: 'capex', label: 'CapEx' },
    { value: 'mixed', label: 'Mixed' },
];

const APPROVAL_STATUS_OPTIONS = [
    { value: '', label: 'All Approval States' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
];

const getSelectStyles = () => ({
    control: (base, state) => ({
        ...base,
        height: 36,
        minHeight: 36,
        maxHeight: 36,
        borderRadius: 10,
        alignItems: 'center',
        borderColor: state.isFocused ? '#6d28d9' : '#d9e2ee',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(109, 40, 217, 0.09)' : 'none',
        '&:hover': { borderColor: state.isFocused ? '#6d28d9' : '#c3d3e6' },
    }),
    valueContainer: (base) => ({
        ...base,
        minHeight: 34,
        height: 34,
        padding: '0 10px',
        fontSize: '0.8rem',
        display: 'flex',
        alignItems: 'center',
    }),
    indicatorsContainer: (base) => ({ ...base, height: 34, alignItems: 'center' }),
    singleValue: (base) => ({ ...base, color: '#334155', fontSize: '0.8rem', margin: 0, lineHeight: '1.2' }),
    placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: '0.78rem', margin: 0, lineHeight: '1.2' }),
    input: (base) => ({ ...base, margin: 0, paddingTop: 0, paddingBottom: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: 6, color: '#94a3b8' }),
    indicatorSeparator: () => ({ display: 'none' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, borderRadius: 12, overflow: 'hidden', border: '1px solid #dbe4ef' }),
    option: (base, state) => ({
        ...base,
        fontSize: '0.8rem',
        backgroundColor: state.isSelected ? '#ede9fe' : state.isFocused ? '#f5f3ff' : '#fff',
        color: state.isSelected ? '#5b21b6' : '#334155',
    }),
});

const fmtCurrency = (value) => {
    const num = parseFloat(value);
    return Number.isNaN(num) ? '0.00' : num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toLocalIsoDate = (date) => (
    new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().substring(0, 10)
);

const normalizeDateInput = (value) => {
    if (!value) return '';
    if (value instanceof Date) return toLocalIsoDate(value);
    const stringValue = String(value).trim();
    if (stringValue.length >= 10) return stringValue.substring(0, 10);
    return stringValue;
};

const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 28);
    return {
        startDate: toLocalIsoDate(start),
        endDate: toLocalIsoDate(end),
    };
};

const getCurrentFiscalMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const parseActiveStore = (activeStore) => {
    try {
        return (activeStore && activeStore.trim() !== '') ? JSON.parse(activeStore) : null;
    } catch {
        return null;
    }
};

const ExpensesHistory = () => {
    const authCtx = useContext(AuthContext);
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = parseActiveStore(asCtx?.activeStore);
    const storeIdentifier = activeStore?.storeIdentifier || '';
    const defaultDateRange = useMemo(() => getDefaultDateRange(), []);
    const currentFiscalMonth = useMemo(() => getCurrentFiscalMonth(), []);
    const navigate = useNavigate();

    // Mobile only: the whole card becomes tappable (native list pattern,
    // chevron affordance) instead of relying on a full-width View button.
    const [isMobileViewport, setIsMobileViewport] = useState(() => (
        typeof window !== 'undefined' ? window.innerWidth <= 768 : false
    ));

    useEffect(() => {
        const handleViewportResize = () => setIsMobileViewport(window.innerWidth <= 768);
        window.addEventListener('resize', handleViewportResize);
        return () => window.removeEventListener('resize', handleViewportResize);
    }, []);

    const [expenses, setExpenses] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState({});
    const [serverMessage, setServerMessage] = useState('');

    const [categories, setCategories] = useState([]);
    const [catLoading, setCatLoading] = useState(false);

    const [pageSize, setPageSize] = useState(20);
    const [sortBy, setSortBy] = useState('expenseDate');
    const [sortOrder, setSortOrder] = useState('DESC');
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    // Mobile only: the desktop filter grid is too dense for a phone, so on
    // mobile it lives in a "Filters" bottom sheet instead of always-visible.
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
    const nextCursorRef = useRef(null);
    const hasMoreRef = useRef(false);
    const filtersRef = useRef(null);

    const [filters, setFilters] = useState({
        fiscalPeriod: '',
        startDate: defaultDateRange.startDate,
        endDate: defaultDateRange.endDate,
        paymentStatus: '',
        expenseType: '',
        approvalStatus: '',
        vendorIdentifier: '',
        storeCategoryIdentifier: '',
        storeSubcategoryIdentifier: '',
        minAmount: '',
        maxAmount: '',
        search: '',
    });

    useEffect(() => {
        filtersRef.current = filters;
    }, [filters]);

    const buildRequestFilters = useCallback((currentFilters) => {
        const prepared = {};

        if (currentFilters.fiscalPeriod) {
            prepared.fiscalPeriod = currentFilters.fiscalPeriod;
        } else {
            if (currentFilters.startDate) prepared.startDate = currentFilters.startDate;
            if (currentFilters.endDate) prepared.endDate = currentFilters.endDate;
        }

        if (currentFilters.paymentStatus) prepared.paymentStatus = currentFilters.paymentStatus;
        if (currentFilters.expenseType) prepared.expenseType = currentFilters.expenseType;
        if (currentFilters.approvalStatus) prepared.approvalStatus = currentFilters.approvalStatus;
        if (currentFilters.vendorIdentifier.trim()) prepared.vendorIdentifier = currentFilters.vendorIdentifier.trim();
        if (currentFilters.storeCategoryIdentifier) prepared.storeCategoryIdentifier = currentFilters.storeCategoryIdentifier;
        if (currentFilters.storeSubcategoryIdentifier) prepared.storeSubcategoryIdentifier = currentFilters.storeSubcategoryIdentifier;
        if (currentFilters.minAmount !== '') prepared.minAmount = Number(currentFilters.minAmount);
        if (currentFilters.maxAmount !== '') prepared.maxAmount = Number(currentFilters.maxAmount);
        if (currentFilters.search.trim()) prepared.search = currentFilters.search.trim();

        return prepared;
    }, []);

    const fetchCategories = useCallback(async () => {
        if (!storeIdentifier) return;
        setCatLoading(true);
        try {
            const response = await fetch(BASE_URL_API + 'fetchStoreExpensesCategoryData', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx?.token, storeIdentifier, includeInactive: false }),
            });
            const result = await response.json();
            if (result?.status === 'success') {
                setCategories(Array.isArray(result?.response?.categories) ? result.response.categories : []);
            } else if (result?.message === 'tokenExpired') {
                authCtx.logout();
            } else {
                setCategories([]);
            }
        } catch {
            setCategories([]);
        } finally {
            setCatLoading(false);
        }
    }, [authCtx, storeIdentifier]);

    const fetchExpensesList = useCallback(async ({ append = false } = {}) => {
        if (!storeIdentifier) return;

        if (append) {
            if (!hasMoreRef.current || !nextCursorRef.current) return;
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
            setServerMessage('');
        }

        const requestPayload = {
            token: authCtx?.token,
            storeIdentifier,
            pageSize,
            sort: { sortBy, sortOrder },
        };

        const reqFilters = buildRequestFilters(filtersRef.current || {});
        if (Object.keys(reqFilters).length > 0) requestPayload.filters = reqFilters;
        if (append && nextCursorRef.current) requestPayload.cursor = nextCursorRef.current;

        try {
            const response = await fetch(BASE_URL_API + 'fetchExpensesList', {
                method: 'POST',
                body: JSON.stringify(requestPayload),
            });

            const result = await response.json();
            if (result?.status === 'success') {
                const list = Array.isArray(result?.response?.expenses) ? result.response.expenses : [];
                setExpenses((prev) => (append ? [...prev, ...list] : list));
                const resolvedHasMore = Boolean(result?.response?.hasMore);
                const resolvedNextCursor = result?.response?.nextCursor || null;
                setHasMore(resolvedHasMore);
                setNextCursor(resolvedNextCursor);
                hasMoreRef.current = resolvedHasMore;
                nextCursorRef.current = resolvedNextCursor;
                setAppliedFilters(result?.response?.appliedFilters || {});
                setServerMessage(result?.message || '');
            } else if (result?.message === 'tokenExpired') {
                authCtx.logout();
            } else {
                toast.error(result?.message || 'Unable to fetch expenses list', { position: 'top-center', hideProgressBar: true, autoClose: 3000 });
                if (!append) {
                    setExpenses([]);
                    setHasMore(false);
                    setNextCursor(null);
                    hasMoreRef.current = false;
                    nextCursorRef.current = null;
                    setAppliedFilters({});
                }
            }
        } catch {
            toast.error('Unable to connect to server.', { position: 'top-center', hideProgressBar: true, autoClose: 3000 });
            if (!append) {
                setExpenses([]);
                setHasMore(false);
                setNextCursor(null);
                hasMoreRef.current = false;
                nextCursorRef.current = null;
                setAppliedFilters({});
            }
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [authCtx, buildRequestFilters, pageSize, sortBy, sortOrder, storeIdentifier]);

    useEffect(() => {
        hasMoreRef.current = hasMore;
    }, [hasMore]);

    useEffect(() => {
        nextCursorRef.current = nextCursor;
    }, [nextCursor]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    useEffect(() => {
        if (!storeIdentifier) return;
        fetchExpensesList({ append: false });
    }, [storeIdentifier, pageSize, sortBy, sortOrder, fetchExpensesList]);

    const categoryOptions = useMemo(() => (
        categories
            .filter((category) => category?.isActive === 1 || category?.isActive === '1')
            .sort((a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0))
            .map((category) => ({ value: category.storeCategoryIdentifier, label: category.categoryLabel }))
    ), [categories]);

    const subcategoryOptions = useMemo(() => {
        const selectedCategory = categories.find((category) => category.storeCategoryIdentifier === filters.storeCategoryIdentifier);
        const subcategories = Array.isArray(selectedCategory?.subcategories) ? selectedCategory.subcategories : [];
        return subcategories
            .filter((subcategory) => subcategory?.isActive === 1 || subcategory?.isActive === '1')
            .sort((a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0))
            .map((subcategory) => ({ value: subcategory.storeSubcategoryIdentifier, label: subcategory.subcategoryLabel }));
    }, [categories, filters.storeCategoryIdentifier]);

    const appliedFilterChips = useMemo(() => (
        Object.entries(appliedFilters || {}).map(([key, value]) => ({ key, value: String(value) }))
    ), [appliedFilters]);

    const summary = useMemo(() => {
        const totalAmount = expenses.reduce((sum, expense) => sum + (parseFloat(expense?.totalExpense) || 0), 0);
        const totalDue = expenses.reduce((sum, expense) => sum + (parseFloat(expense?.balanceDue) || 0), 0);
        const paidCount = expenses.filter((expense) => expense?.paymentStatus === 'paid').length;
        return { totalAmount, totalDue, paidCount };
    }, [expenses]);

    const onChangeFilter = (field, value) => {
        setFilters((prev) => {
            const next = { ...prev, [field]: value };
            if (field === 'fiscalPeriod' && value) {
                next.startDate = '';
                next.endDate = '';
            }
            if (field === 'storeCategoryIdentifier') {
                next.storeSubcategoryIdentifier = '';
            }
            return next;
        });
    };

    const closeDateModal = () => {
        setIsDatePickerOpen(false);
    };

    const changeDateHandler = (startDate, endDate) => {
        const normalizedStartDate = normalizeDateInput(startDate);
        const normalizedEndDate = normalizeDateInput(endDate);
        setFilters((prev) => {
            if (prev.startDate === normalizedStartDate && prev.endDate === normalizedEndDate && prev.fiscalPeriod === '') {
                return prev;
            }
            return {
                ...prev,
                fiscalPeriod: '',
                startDate: normalizedStartDate,
                endDate: normalizedEndDate,
            };
        });
    };

    const onApplyFilters = () => {
        if (filters.minAmount !== '' && Number(filters.minAmount) < 0) {
            toast.error('Minimum amount cannot be negative.', { position: 'top-center', hideProgressBar: true, autoClose: 2600 });
            return;
        }
        if (filters.maxAmount !== '' && Number(filters.maxAmount) < 0) {
            toast.error('Maximum amount cannot be negative.', { position: 'top-center', hideProgressBar: true, autoClose: 2600 });
            return;
        }
        if (filters.minAmount !== '' && filters.maxAmount !== '' && Number(filters.minAmount) > Number(filters.maxAmount)) {
            toast.error('Minimum amount cannot be greater than maximum amount.', { position: 'top-center', hideProgressBar: true, autoClose: 3000 });
            return;
        }
        filtersRef.current = filters;
        setNextCursor(null);
        nextCursorRef.current = null;
        hasMoreRef.current = false;
        fetchExpensesList({ append: false });
        setIsMobileFiltersOpen(false);
    };

    const onResetFilters = () => {
        const freshDefaultDateRange = getDefaultDateRange();
        setFilters({
            fiscalPeriod: '',
            startDate: freshDefaultDateRange.startDate,
            endDate: freshDefaultDateRange.endDate,
            paymentStatus: '',
            expenseType: '',
            approvalStatus: '',
            vendorIdentifier: '',
            storeCategoryIdentifier: '',
            storeSubcategoryIdentifier: '',
            minAmount: '',
            maxAmount: '',
            search: '',
        });
        setSortBy('expenseDate');
        setSortOrder('DESC');
        setPageSize(20);
        nextCursorRef.current = null;
        hasMoreRef.current = false;
    };

    const onLoadMore = () => {
        fetchExpensesList({ append: true });
    };

    const paymentStatusClass = (status) => {
        if (status === 'paid') return styles.statusPaid;
        if (status === 'partial') return styles.statusPartial;
        return styles.statusUnpaid;
    };

    const approvalStatusClass = (status) => {
        if (status === 'approved') return styles.approved;
        if (status === 'rejected') return styles.rejected;
        return styles.pending;
    };

    if (!storeIdentifier) {
        return (
            <div className={styles.noStoreWrapper}>
                <div className={styles.noStoreCard}>
                    <ReceiptText size={34} className={styles.noStoreIcon} />
                    <h3>No Store Selected</h3>
                    <p>Please choose a store from top switcher to view expense history.</p>
                    <Link to="/expenses" className={styles.backLink}>Back to Expenses Home</Link>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.mainWrapper}>
            <motion.div
                className={styles.heroCard}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
            >
                <div className={styles.heroTopRow}>
                    <h1 className={styles.pageTitle}>Expenses History</h1>
                    {/* Desktop only — mobile already has Add via the bottom nav's + tab */}
                    <Link to="/expenses/add" className={styles.addBtn}>Add New Expense</Link>
                </div>

                <div className={styles.statsRow}>
                    <div className={styles.statChip}><ReceiptText size={14} /> Records: <strong>{expenses.length}</strong></div>
                    <div className={styles.statChip}><IndianRupee size={14} /> Listed Total: <strong>₹{fmtCurrency(summary.totalAmount)}</strong></div>
                    <div className={styles.statChip}><FileSearch size={14} /> Balance Due: <strong>₹{fmtCurrency(summary.totalDue)}</strong></div>
                    <div className={styles.statChip}><Tag size={14} /> Paid Bills: <strong>{summary.paidCount}</strong></div>
                </div>
            </motion.div>

            {/* Mobile only: quick date access + Filters, one compact row instead
                of dates being buried a tap deeper inside the filter sheet. */}
            <div className={styles.mobileQuickBar}>
                <button type="button" className={styles.mobileDateBtn} onClick={() => setIsDatePickerOpen(true)}>
                    <Calendar size={13} />
                    {filters.startDate && filters.endDate ? `${filters.startDate} → ${filters.endDate}` : 'Select dates'}
                </button>

                <button type="button" className={styles.mobileFilterTrigger} onClick={() => setIsMobileFiltersOpen(true)}>
                    <SlidersHorizontal size={14} /> Filters
                    {appliedFilterChips.length > 0 && <span className={styles.mobileFilterCount}>{appliedFilterChips.length}</span>}
                </button>
            </div>

            {isMobileFiltersOpen && (
                <div className={styles.filterBackdrop} onClick={() => setIsMobileFiltersOpen(false)} />
            )}

            <div className={`${styles.filterPanel} ${isMobileFiltersOpen ? styles.filterPanelOpen : ''}`}>
                <div className={styles.filterPanelHeader}>
                    <div className={styles.filterTitle}><SlidersHorizontal size={14} /> Filter & Sort Criteria</div>
                    <div className={styles.filterActions}>
                        <button type="button" className={styles.secondaryBtn} onClick={onResetFilters}><RefreshCcw size={13} /> Reset</button>
                        <button type="button" className={styles.primaryBtn} onClick={onApplyFilters}><Filter size={13} /> Apply</button>
                        <button type="button" className={styles.mobileFilterClose} onClick={() => setIsMobileFiltersOpen(false)} aria-label="Close filters">
                            <X size={16} />
                        </button>
                    </div>
                </div>

                <div className={styles.filterGrid}>
                    <label className={styles.field}>
                        <span>Fiscal Period (fastest)</span>
                        <input
                            type="month"
                            value={filters.fiscalPeriod}
                            max={currentFiscalMonth}
                            onChange={(e) => onChangeFilter('fiscalPeriod', e.target.value > currentFiscalMonth ? currentFiscalMonth : e.target.value)}
                        />
                    </label>
                    {/* Mobile has this already via the quick bar above the sheet — hidden
                        here to avoid showing the same control twice. Desktop keeps it. */}
                    <label className={`${styles.field} ${styles.dateRangeField}`}>
                        <span><Calendar size={12} /> Date Range</span>
                        <button type="button" className={styles.dateRangeBtn} onClick={() => setIsDatePickerOpen(true)}>
                            {filters.startDate && filters.endDate ? `${filters.startDate} → ${filters.endDate}` : 'Select date range'}
                        </button>
                    </label>

                    <label className={styles.field}>
                        <span>Payment Status</span>
                        <Select
                            options={PAYMENT_STATUS_OPTIONS}
                            value={PAYMENT_STATUS_OPTIONS.find((option) => option.value === filters.paymentStatus)}
                            onChange={(option) => onChangeFilter('paymentStatus', option?.value || '')}
                            styles={getSelectStyles()}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            menuPosition="fixed"
                        />
                    </label>

                    <label className={styles.field}>
                        <span>Expense Type</span>
                        <Select
                            options={EXPENSE_TYPE_OPTIONS}
                            value={EXPENSE_TYPE_OPTIONS.find((option) => option.value === filters.expenseType)}
                            onChange={(option) => onChangeFilter('expenseType', option?.value || '')}
                            styles={getSelectStyles()}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            menuPosition="fixed"
                        />
                    </label>

                    <label className={styles.field}>
                        <span>Approval Status</span>
                        <Select
                            options={APPROVAL_STATUS_OPTIONS}
                            value={APPROVAL_STATUS_OPTIONS.find((option) => option.value === filters.approvalStatus)}
                            onChange={(option) => onChangeFilter('approvalStatus', option?.value || '')}
                            styles={getSelectStyles()}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            menuPosition="fixed"
                        />
                    </label>

                    <label className={styles.field}>
                        <span>Store Category</span>
                        <Select
                            options={[{ value: '', label: catLoading ? 'Loading...' : 'All Categories' }, ...categoryOptions]}
                            value={[{ value: '', label: 'All Categories' }, ...categoryOptions].find((option) => option.value === filters.storeCategoryIdentifier) || null}
                            onChange={(option) => onChangeFilter('storeCategoryIdentifier', option?.value || '')}
                            isDisabled={catLoading}
                            styles={getSelectStyles()}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            menuPosition="fixed"
                        />
                    </label>

                    <label className={styles.field}>
                        <span>Store Subcategory</span>
                        <Select
                            options={[{ value: '', label: 'All Subcategories' }, ...subcategoryOptions]}
                            value={[{ value: '', label: 'All Subcategories' }, ...subcategoryOptions].find((option) => option.value === filters.storeSubcategoryIdentifier) || null}
                            onChange={(option) => onChangeFilter('storeSubcategoryIdentifier', option?.value || '')}
                            isDisabled={!filters.storeCategoryIdentifier}
                            styles={getSelectStyles()}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            menuPosition="fixed"
                        />
                    </label>

                    {/* Mobile only: vendor lookup is a power-user desktop filter,
                        dropped from the mobile sheet to keep it focused. */}
                    <label className={`${styles.field} ${styles.vendorField}`}>
                        <span>Vendor Identifier</span>
                        <input type="text" value={filters.vendorIdentifier} onChange={(e) => onChangeFilter('vendorIdentifier', e.target.value)} placeholder="VENDOR_001" maxLength={60} />
                    </label>

                    <label className={styles.field}>
                        <span>Min Amount (₹)</span>
                        <input type="number" value={filters.minAmount} onChange={(e) => onChangeFilter('minAmount', e.target.value)} min="0" step="0.01" placeholder="500" />
                    </label>

                    <label className={styles.field}>
                        <span>Max Amount (₹)</span>
                        <input type="number" value={filters.maxAmount} onChange={(e) => onChangeFilter('maxAmount', e.target.value)} min="0" step="0.01" placeholder="50000" />
                    </label>

                    <label className={styles.field}>
                        <span><Search size={12} /> Search Notes / Invoice</span>
                        <input type="text" value={filters.search} onChange={(e) => onChangeFilter('search', e.target.value)} placeholder="wella / INV-2025-441" maxLength={120} />
                    </label>

                    <label className={styles.field}>
                        <span><ArrowDownUp size={12} /> Sort By</span>
                        <Select
                            options={SORT_BY_OPTIONS}
                            value={SORT_BY_OPTIONS.find((option) => option.value === sortBy)}
                            onChange={(option) => setSortBy(option?.value || 'expenseDate')}
                            styles={getSelectStyles()}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            menuPosition="fixed"
                        />
                    </label>

                    <label className={styles.field}>
                        <span>Sort Order</span>
                        <Select
                            options={SORT_ORDER_OPTIONS}
                            value={SORT_ORDER_OPTIONS.find((option) => option.value === sortOrder)}
                            onChange={(option) => setSortOrder(option?.value || 'DESC')}
                            styles={getSelectStyles()}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            menuPosition="fixed"
                        />
                    </label>

                    <label className={styles.field}>
                        <span>Page Size</span>
                        <Select
                            options={PAGE_SIZE_OPTIONS.map((value) => ({ value, label: `${value} rows` }))}
                            value={{ value: pageSize, label: `${pageSize} rows` }}
                            onChange={(option) => setPageSize(Number(option?.value || 20))}
                            styles={getSelectStyles()}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            menuPosition="fixed"
                        />
                    </label>
                </div>
            </div>

            <DateRangeSelector
                isOpen={isDatePickerOpen}
                closeModal={closeDateModal}
                changeDateHandler={changeDateHandler}
                initialStartDate={filters.startDate || undefined}
                initialEndDate={filters.endDate || undefined}
            />

            {appliedFilterChips.length > 0 && (
                <div className={styles.appliedFiltersBar}>
                    {appliedFilterChips.map((chip) => (
                        <span key={chip.key} className={styles.filterChip}>{chip.key}: {chip.value}</span>
                    ))}
                </div>
            )}

            <div className={styles.tableCard}>
                <div className={styles.tableMeta}>
                    <span><CalendarDays size={12} /> Returned: {expenses.length}</span>
                    <span>{serverMessage || 'Ready'}</span>
                </div>

                <div className={styles.tableScroll}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Bill Date</th>
                                <th>Expense ID</th>
                                <th>Invoice</th>
                                <th>Total</th>
                                <th>GST</th>
                                <th>Balance</th>
                                <th>Payment</th>
                                <th>Type</th>
                                <th>Approval</th>
                                <th>Items</th>
                                <th>Notes</th>
                                <th>Attachment</th>
                                <th>Created On</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && (
                                <tr>
                                    <td colSpan={14} className={styles.loadingCell}>
                                        <LoaderCircle size={16} className={styles.spin} /> Loading expenses...
                                    </td>
                                </tr>
                            )}

                            {!isLoading && expenses.length === 0 && (
                                <tr>
                                    <td colSpan={14} className={styles.emptyCell}>
                                        <FileSearch size={18} /> No records found for selected criteria.
                                    </td>
                                </tr>
                            )}

                            {!isLoading && expenses.map((expense) => (
                                <tr
                                    key={expense?.expenseIdentifier}
                                    className={isMobileViewport && expense?.expenseIdentifier ? styles.rowTappable : ''}
                                    onClick={() => {
                                        if (isMobileViewport && expense?.expenseIdentifier) {
                                            navigate(`/expenses/history/${expense.expenseIdentifier}`);
                                        }
                                    }}
                                >
                                    <td>{expense?.expenseDate || '—'}</td>
                                    <td className={styles.codeCell}>{expense?.expenseIdentifier || '—'}</td>
                                    <td>{expense?.vendorInvoiceNumber || '—'}</td>
                                    <td className={styles.money}>₹{fmtCurrency(expense?.totalExpense)}</td>
                                    <td className={styles.money}>₹{fmtCurrency(expense?.gstAmount)}</td>
                                    <td className={styles.money}>₹{fmtCurrency(expense?.balanceDue)}</td>
                                    <td><span className={`${styles.badge} ${paymentStatusClass(expense?.paymentStatus)}`}>{expense?.paymentStatus || '—'}</span></td>
                                    <td className={styles.upper}>{expense?.expenseType || '—'}</td>
                                    <td><span className={`${styles.badge} ${approvalStatusClass(expense?.approvalStatus)}`}>{expense?.approvalStatus || '—'}</span></td>
                                    <td>{expense?.totalItems ?? 0}</td>
                                    <td className={styles.notesCell}>{expense?.expenseNotes || '—'}</td>
                                    <td>
                                        {expense?.attachmentUrl
                                            ? <a href={expense.attachmentUrl} target="_blank" rel="noreferrer" className={styles.attachmentLink}><Link2 size={12} /> Open</a>
                                            : '—'}
                                    </td>
                                    <td>{expense?.createdOn || '—'}</td>
                                    <td>
                                        {expense?.expenseIdentifier
                                            ? (
                                                <Link to={`/expenses/history/${expense.expenseIdentifier}`} className={styles.viewBtn} onClick={(e) => e.stopPropagation()}>
                                                    <span className={styles.viewBtnLabel}>View</span>
                                                    <ChevronRight size={16} className={styles.viewChevronIcon} />
                                                </Link>
                                            )
                                            : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className={styles.loadMoreBar}>
                    <button
                        type="button"
                        className={styles.loadMoreBtn}
                        onClick={onLoadMore}
                        disabled={!hasMore || isLoadingMore || isLoading}
                    >
                        {isLoadingMore
                            ? <><LoaderCircle size={14} className={styles.spin} /> Loading more</>
                            : hasMore
                                ? 'Load More'
                                : 'No More Records'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExpensesHistory;